"use client";

// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
import { leadCtaClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import Image from "next/image";
import { type CSSProperties, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import * as z from "@/lib/propSchema";
import { LEAD_DROP_VARIANTS } from "../../../constants";

const MetadataSchema = z.object({
  id: z.union([z.number(), z.string()]),
  meta_image: z.string().optional().default(""),
  meta_alt_image: z.string().optional().default(""),
  meta_title: z.string().optional().default(""),
  meta_title_color: z.string().optional().default("#171719"),
  meta_sub_title: z.string().optional().default(""),
  meta_sub_title_color: z.string().optional().default("#505050"),
  meta_priority: z.coerce.number().optional().default(0),
});

const SectionSchema = z
  .object({
    pre_title: z.string().optional().default(""),
    pre_title_color: z.string().optional().default("#E15518"),
    title: z.string().optional().default(""),
    title_start_color: z.string().optional().default("#171719"),
    subtitle: z.string().optional().default(""),
    subtitle_color: z.string().optional().default("#373737"),
    section_background_image: z.string().optional().default(""),
    section_overlay_rgba: z.string().optional().default("rgba(255,255,255,0.92)"),
    card_hover_background_color: z.string().optional().default("#E15518"),
    button_text: z.string().optional().default(""),
    button_color: z.string().optional().default("#DB5803"),
    button_text_color: z.string().optional().default("#FFFFFF"),
    button_icon_url: z.string().optional().default(""),
    button_icon_alt: z.string().optional().default(""),
    metadata: z.array(MetadataSchema).optional().default([]),
    status: z.union([z.boolean(), z.number()]).optional(),
  })
  .transform((d) => ({
    ...d,
    metadata: [...(d.metadata || [])].sort(
      (a, b) => (a.meta_priority || 0) - (b.meta_priority || 0),
    ),
  }));

type CardRow = z.infer<typeof MetadataSchema>;
type SectionParsed = z.infer<typeof SectionSchema>;

function CurriculumCard({
  row,
  hoverBg,
}: {
  row: CardRow;
  hoverBg: string;
}) {
  const titleColor = row.meta_title_color?.trim() || "#171719";
  const bodyColor = row.meta_sub_title_color?.trim() || "#505050";

  return (
    <article
      className={cn(
        "group relative flex flex-col rounded-[12px] w-full bg-white",
        "shadow-[0px_4px_21.3px_0px_rgba(0,0,0,0.12)]",
        "transition-[background-color,box-shadow] duration-200 ease-out",
        "hover:shadow-[0px_8px_28px_0px_rgba(0,0,0,0.18)]",
        "max-w-[304px] mx-auto min-h-[260px] p-5 gap-4",
        "min-[640px]:max-w-none min-[640px]:mx-0 min-[640px]:min-h-[268px] min-[640px]:p-6 min-[640px]:gap-5",
      )}
      style={
        {
          ["--pb3-hover" as string]: hoverBg,
          ["--pb3-title" as string]: titleColor,
          ["--pb3-body" as string]: bodyColor,
        } as CSSProperties
      }
    >
      <div
        className="absolute inset-0 rounded-[12px] bg-white transition-colors duration-200 ease-out group-hover:bg-[var(--pb3-hover)]"
        aria-hidden
      />
      <div className="relative z-[1] flex flex-col items-center gap-4 min-[640px]:items-start min-[640px]:gap-5 h-full">
        <div className="relative flex shrink-0 w-12 h-12 items-center justify-center mx-auto min-[640px]:mx-0 min-[640px]:w-14 min-[640px]:h-14">
          {row.meta_image ? (
            <Image
              src={row.meta_image}
              alt={row.meta_alt_image || row.meta_title || "Benefit"}
              width={56}
              height={56}
              className={cn(
                "object-contain size-12 min-[640px]:size-14 shrink-0",
                "transition-[filter] duration-200 ease-out",
                "group-hover:brightness-0 group-hover:invert",
              )}
            />
          ) : (
            <span
              className="block size-12 min-[640px]:size-14 rounded-md bg-black/[0.06] group-hover:bg-white/30"
              aria-hidden
            />
          )}
        </div>

        <div className="flex flex-col gap-2 min-[640px]:gap-3 min-w-0 flex-1 text-center min-[640px]:text-left">
          <h3
            className={cn(
              "font-semibold text-[16px] leading-snug min-[640px]:text-[16px] min-[640px]:leading-[22px]",
              "text-[color:var(--pb3-title)] group-hover:text-white",
              "transition-colors duration-200",
            )}
          >
            {row.meta_title}
          </h3>
          <p
            className={cn(
              "text-[14px] min-[640px]:text-[14px] leading-[20px] font-normal",
              "text-[color:var(--pb3-body)] group-hover:text-white",
              "transition-colors duration-200",
            )}
          >
            {row.meta_sub_title}
          </p>
        </div>
      </div>
    </article>
  );
}

function ArrowUpRight() {
  return (
    <svg
      aria-hidden
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      className="size-5 shrink-0"
    >
      <path
        d="M7 17L17 7M9 7h8v8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Mobile-only card. Since touch devices have no hover, the card is tap-to-flip:
 * the front shows the icon + title + "View More", tapping rotates the card 180°
 * to a back face that reveals the description + "View less". Desktop keeps the
 * existing hover behavior via <CurriculumCard/> — this component is never shown
 * at >=640px.
 */
function MobileFlipCard({ row, hoverBg }: { row: CardRow; hoverBg: string }) {
  const [flipped, setFlipped] = useState(false);
  const titleColor = row.meta_title_color?.trim() || "#171719";
  const desc = row.meta_sub_title?.trim() || "";

  const icon = row.meta_image ? (
    <Image
      src={row.meta_image}
      alt={row.meta_alt_image || row.meta_title || "Benefit"}
      width={42}
      height={42}
      className="object-contain size-[42px] shrink-0"
    />
  ) : (
    <span className="block size-[42px] rounded-md bg-black/[0.06]" aria-hidden />
  );

  // No description -> nothing to flip to, render a plain static card.
  if (!desc) {
    return (
      <article className="relative flex h-[212px] w-full flex-col gap-3 overflow-hidden rounded-[12px] bg-white p-4 shadow-[0px_4px_21.3px_0px_rgba(0,0,0,0.12)]">
        <div className="relative flex size-[42px] shrink-0 items-center justify-center">
          {icon}
        </div>
        <p
          className="font-semibold text-[16px] leading-[20px]"
          style={{ color: titleColor }}
        >
          {row.meta_title}
        </p>
      </article>
    );
  }

  const toggle = () => setFlipped((f) => !f);

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={flipped}
      aria-label={row.meta_title}
      onClick={toggle}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          toggle();
        }
      }}
      className={cn(
        "relative h-[212px] w-full cursor-pointer select-none rounded-[12px] [perspective:1000px]",
        "outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-black/40",
      )}
    >
      {/*
        Both faces are stacked in one grid cell (grid-area 1/1) at a fixed
        168x212 size to match the reference. transform-style / backface-visibility
        are set inline (with -webkit- prefixes) because the Tailwind v4 +
        Lightning CSS pipeline does not emit the webkit prefixes that iOS Safari
        < 15.4 needs, and without them the back face bleeds over the front.
      */}
      <div
        className={cn(
          "grid h-full w-full [&>*]:[grid-area:1/1]",
          "motion-safe:transition-transform motion-safe:duration-500 motion-safe:ease-out",
          flipped && "[transform:rotateY(180deg)]",
        )}
        style={{
          transformStyle: "preserve-3d",
          WebkitTransformStyle: "preserve-3d",
        }}
      >
        {/* Front */}
        <article
          className="flex h-full w-full flex-col gap-3 overflow-hidden rounded-[12px] bg-white p-4 shadow-[0px_4px_21.3px_0px_rgba(0,0,0,0.12)]"
          style={{
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
          }}
        >
          <div className="relative flex size-[42px] shrink-0 items-center justify-center">
            {icon}
          </div>
          <p
            className="font-semibold text-[16px] leading-[20px]"
            style={{ color: titleColor }}
          >
            {row.meta_title}
          </p>
          <div
            className="mt-auto flex items-center gap-1 font-semibold text-[14px]"
            style={{ color: hoverBg }}
          >
            <span>View More</span>
            <ArrowUpRight />
          </div>
        </article>

        {/* Back */}
        <article
          className="flex h-full w-full flex-col gap-2 overflow-hidden rounded-[12px] p-4 text-white shadow-[0px_4px_21.3px_0px_rgba(0,0,0,0.12)]"
          style={{
            backgroundColor: hoverBg,
            transform: "rotateY(180deg)",
            WebkitTransform: "rotateY(180deg)",
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
          }}
        >
          <p className="text-[14px] leading-[20px] font-normal text-white">
            {desc}
          </p>
          <div className="mt-auto flex items-center gap-1 font-semibold text-[14px] text-white">
            <span>View less</span>
            <ArrowUpRight />
          </div>
        </article>
      </div>
    </div>
  );
}

export default function ProgramBenefitsGrid3({ data }: { data: any }) {
  const ref = useRef<HTMLElement>(null);
  const dispatch = useDispatch();
  const raw = data?.data ?? data ?? {};
  const parsed = useMemo(() => SectionSchema.safeParse(raw), [raw]);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "program_benefits_grid_3");
  //   }
  // }, []);

  if (!parsed.success) {
    return null;
  }

  const section = parsed.data as SectionParsed;
  const isOff =
    section.status === false ||
    section.status === 0 ||
    String(section.status) === "0";

  if (isOff) {
    return null;
  }

  const rows = section.metadata?.filter((m) => m.meta_title?.trim()) ?? [];
  const hasHeader =
    Boolean(section.title?.trim()) ||
    Boolean(section.pre_title?.trim()) ||
    Boolean(section.subtitle?.trim());

  if (rows.length === 0 && !hasHeader) {
    return null;
  }

  const overlay = section.section_overlay_rgba || "rgba(255,255,255,0.92)";
  const line1Color = section.title_start_color || "#171719";
  const line2Color = section.pre_title_color || "#E15518";
  const subtitleColor = section.subtitle_color?.trim() || "#373737";
  const hoverBg = section.card_hover_background_color?.trim() || "#E15518";

  const ctaText = section.button_text?.trim() || "";
  const ctaBg = section.button_color?.trim() || "#DB5803";
  const ctaColor = section.button_text_color?.trim() || "#FFFFFF";
  const ctaIcon = section.button_icon_url?.trim() || "";
  const ctaIconAlt = section.button_icon_alt?.trim() || ctaText;

  const handleCtaClick = () => {
    leadCtaClick({
      section: "program_benefits_grid_3",
      component: "cta_button",
      label: ctaText,
    });
    dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
  };

  return (
    <section
      ref={ref}
      className={cn(
        "[&_*]:![font-family:Poppins,sans-serif]",
        "relative w-full flex flex-col items-center justify-center overflow-hidden",
        "gap-8 py-8 px-4",
        "min-[640px]:gap-12 min-[640px]:py-12 min-[640px]:px-8 min-[1024px]:py-[72px] min-[1024px]:px-[99px]",
      )}
      style={{ fontFamily: "Poppins, sans-serif" }}
    >
      {section.section_background_image ? (
        <>
          <div className="absolute inset-0 pointer-events-none" aria-hidden>
            <div className="absolute inset-0 overflow-hidden relative">
              <Image
                src={section.section_background_image}
                alt=""
                fill
                className="object-cover object-center scale-[1.12] min-[640px]:scale-105"
                sizes="100vw"
                priority={false}
              />
            </div>
            <div
              className="absolute inset-0"
              style={{ backgroundColor: overlay }}
            />
          </div>
        </>
      ) : (
        <div
          className="absolute inset-0 pointer-events-none bg-white"
          aria-hidden
        />
      )}

      {(section.title || section.pre_title || section.subtitle) && (
        <div
          className={cn(
            "relative z-[1] w-full max-w-[1100px] min-[1024px]:max-w-7xl mx-auto",
            "flex flex-col items-center text-center gap-2 min-[640px]:gap-3",
          )}
        >
          {(section.title || section.pre_title) && (
            <h2
              className={cn(
                "w-full max-w-5xl mx-auto font-semibold text-center",
                "text-[24px] leading-[1.25] tracking-[-0.5px]",
                "min-[640px]:text-[32px] min-[640px]:leading-tight min-[640px]:tracking-normal",
              )}
            >
              {section.title?.trim() ? (
                <span style={{ color: line1Color }}>{section.title.trim()}</span>
              ) : null}
              {section.title?.trim() && section.pre_title?.trim() ? " " : null}
              {section.pre_title?.trim() ? (
                <span style={{ color: line2Color }}>
                  {section.pre_title.trim()}
                </span>
              ) : null}
            </h2>
          )}
          {section.subtitle ? (
            <p
              className={cn(
                "w-full max-w-3xl mx-auto font-normal text-[#1f1f1f] text-center",
                "min-[1024px]:max-w-none",
                "text-[16px] leading-[22px] min-[640px]:leading-normal",
              )}
              style={{
                color: subtitleColor,
                fontFamily: "Poppins, sans-serif",
              }}
            >
              {section.subtitle}
            </p>
          ) : null}
          {ctaText && (
            <div className="mt-2 min-[640px]:mt-3 flex w-full justify-center">
              <button
                type="button"
                onClick={handleCtaClick}
                className={cn(
                  "inline-flex items-center justify-center gap-2 cursor-pointer",
                  "rounded-[10px] font-semibold transition-[transform,box-shadow] duration-200 ease-out",
                  "px-6 py-3 text-[15px] min-[640px]:px-8 min-[640px]:py-4 min-[640px]:text-[16px]",
                  "hover:-translate-y-[1px] hover:shadow-[0px_8px_20px_rgba(0,0,0,0.15)]",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                )}
                style={{ backgroundColor: ctaBg, color: ctaColor }}
              >
                <span>{ctaText}</span>
                {ctaIcon ? (
                  <Image
                    src={ctaIcon}
                    alt={ctaIconAlt}
                    width={18}
                    height={18}
                    className="size-[18px] shrink-0 object-contain"
                  />
                ) : (
                  <svg
                    aria-hidden
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="size-[18px] shrink-0"
                  >
                    <path
                      d="M5 12h14M13 5l7 7-7 7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {rows.length > 0 && (
        <div
          className={cn(
            "relative z-[1] w-full max-w-5xl mx-auto flex flex-col gap-8 min-[640px]:gap-10",
          )}
        >
          <div
            className={cn(
              // 2 cards per row on every breakpoint; a lone last card centers
              // itself via flex-wrap + justify-center.
              "flex flex-row flex-wrap justify-center items-stretch gap-4 w-full",
              "min-[640px]:gap-8",
            )}
          >
            {rows.map((row) => (
              <div
                key={String(row.id)}
                className={cn(
                  "flex basis-[calc((100%-1rem)/2)] max-w-[168px]",
                  // >=640px the card is capped at the 264px design width. Kept as
                  // a cap (not a fixed width) so the fluid basis still shrinks it
                  // in the narrow 1024-1055px band instead of wrapping 3-up to 2+1.
                  "min-[640px]:max-w-[264px] min-[640px]:basis-[calc((100%-2rem)/2)]",
                  "min-[1024px]:basis-[calc((100%-4rem)/3)]",
                )}
              >
                {/* Tablet / desktop (>=640px): unchanged hover card */}
                <div className="hidden w-full min-[640px]:flex">
                  <CurriculumCard row={row} hoverBg={hoverBg} />
                </div>
                {/* Mobile (<640px): tap to flip and reveal the description */}
                <div className="flex w-full min-[640px]:hidden">
                  <MobileFlipCard row={row} hoverBg={hoverBg} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
